// Which font FILE the browser uses for a piece of editor text — so the PDF can
// draw the same letters as vector outlines (owner, 2026-10-17 — #1236: text
// printed as a transparent picture showed a dark box on the sky; printers
// flatten the transparency). The editor's faces come from the Google Fonts
// stylesheets the app links (index.html); this reads their @font-face rules
// and applies the CSS font-matching rules the browser applies. Pure.
// Guard: vectorFaces.test.ts

export interface FontFaceRule {
  family: string;
  style: 'normal' | 'italic';
  /** Weight range the file covers (a static file: min === max). */
  wMin: number;
  wMax: number;
  /** unicode-range as [from, to] pairs; empty = every character. */
  ranges: Array<[number, number]>;
  url: string;
}

const unq = (s: string) => s.trim().replace(/^['"]|['"]$/g, '').trim();

/** The @font-face rules of a Google Fonts css2 stylesheet. */
export function parseFontFaceCss(css: string): FontFaceRule[] {
  const out: FontFaceRule[] = [];
  for (const m of css.matchAll(/@font-face\s*\{([^}]*)\}/g)) {
    const body = m[1];
    const get = (k: string) => new RegExp(`${k}\\s*:\\s*([^;]+);`, 'i').exec(body)?.[1]?.trim() ?? '';
    const family = unq(get('font-family'));
    const url = /url\(\s*['"]?([^'")]+)['"]?\s*\)/.exec(get('src'))?.[1] ?? '';
    if (!family || !url) continue;
    const w = get('font-weight').split(/\s+/).map(Number).filter((n) => Number.isFinite(n) && n > 0);
    const ranges: Array<[number, number]> = [];
    for (const r of get('unicode-range').split(',').map((x) => x.trim()).filter(Boolean)) {
      const mm = /^U\+([0-9A-F?]+)(?:-([0-9A-F]+))?$/i.exec(r);
      if (!mm) continue;
      if (mm[1].includes('?')) { ranges.push([parseInt(mm[1].replace(/\?/g, '0'), 16), parseInt(mm[1].replace(/\?/g, 'F'), 16)]); continue; }
      const a = parseInt(mm[1], 16);
      ranges.push([a, mm[2] ? parseInt(mm[2], 16) : a]);
    }
    out.push({
      family,
      style: /italic|oblique/i.test(get('font-style')) ? 'italic' : 'normal',
      wMin: w[0] ?? 400, wMax: w[1] ?? w[0] ?? 400,
      ranges, url,
    });
  }
  return out;
}

const covers = (f: FontFaceRule, cp: number) => !f.ranges.length || f.ranges.some(([a, b]) => cp >= a && cp <= b);

/**
 * CSS Fonts §5.2: the style first (italic wanted, none → normal, drawn
 * slanted), then the weight — inside a range; else for 400–500 up to 500,
 * then down, then up; below 400 down then up; above 500 up then down. The
 * face of that weight whose unicode-range holds the character. Null = no
 * linked face draws it (a system font would) — the caller keeps the raster.
 */
export function pickFace(
  faces: FontFaceRule[], family: string, weight: number, style: 'normal' | 'italic', cp: number,
): { face: FontFaceRule; weight: number; fakeBold: boolean; fakeItalic: boolean } | null {
  const fam = faces.filter((f) => f.family.toLowerCase() === family.toLowerCase());
  if (!fam.length) return null;
  const want = fam.filter((f) => f.style === style);
  const pool = want.length ? want : fam;
  const fakeItalic = style === 'italic' && !want.length;
  const inRange = pool.filter((f) => weight >= f.wMin && weight <= f.wMax);
  let w: number;
  if (inRange.length) w = weight;
  else {
    const ends = [...new Set(pool.flatMap((f) => [f.wMin, f.wMax]))].sort((a, b) => a - b);
    const up = (lim: number) => ends.filter((x) => x > weight && x <= lim)[0];
    const down = () => [...ends].reverse().find((x) => x < weight);
    const upAny = () => ends.find((x) => x > weight);
    if (weight >= 400 && weight <= 500) w = up(500) ?? down() ?? upAny()!;
    else if (weight < 400) w = down() ?? upAny()!;
    else w = upAny() ?? down()!;
  }
  const face = pool.find((f) => w >= f.wMin && w <= f.wMax && covers(f, cp));
  if (!face) return null;
  // Chrome's synthetic bold: 600+ asked, the file is lighter than 600.
  return { face, weight: w, fakeBold: weight >= 600 && w < 600, fakeItalic };
}
